import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import AuthRedirectHandler from "@frontend/app/routing/AuthRedirectHandler.tsx";
import * as Sentry from "@sentry/react";

const isDevelopment = import.meta.env.MODE === "development";
const isProduction = import.meta.env.PROD;

// Only initialize Sentry if DSN is provided
if (import.meta.env.VITE_SENTRY_DSN) {
  if (isDevelopment) {
    console.log("Initializing Sentry with DSN:", import.meta.env.VITE_SENTRY_DSN.replace(/\/\/[^@]+@/, "//***@"));
  } else {
    // Production: Log that Sentry is initializing (without exposing DSN)
    console.log("[Sentry] Initializing in production mode");
  }
  
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
  
  // Setting this option to true will send default PII data to Sentry
  // For example, automatic IP address collection on events
  sendDefaultPii: true,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    // Conditional feedback widget (dev only)
    ...(isDevelopment ? [Sentry.feedbackIntegration()] : []),
  ],

  // Environment configuration - automatically differentiates dev/prod
  environment: isDevelopment ? "development" : "production",
  release: import.meta.env.VITE_COMMIT_SHA || "development-build",

    // Tracing - Capture 100% of the transactions
    tracesSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/mjkochuk\.com\/api/],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Log Sentry initialization for debugging
    beforeSend(event, hint) {
      if (isDevelopment) {
        console.log("[Sentry] Capturing event:", event);
        console.log("[Sentry] Event type:", event.type);
        console.log("[Sentry] Exception:", event.exception);
        console.log("[Sentry] DSN:", import.meta.env.VITE_SENTRY_DSN?.replace(/\/\/[^@]+@/, "//***@"));
      }
      return event;
    },
    
    // Catch network errors during event sending
    beforeSendTransaction(event) {
      return event;
    },
    
    // Log when events are sent successfully
    transportOptions: {
      sendTimeout: 5000,
    },
    
    // Add error handler for transport failures
    onError(error) {
      // Log in both dev and prod to debug production CORS issue
      console.error("[Sentry] Failed to send event:", error);
      console.error("[Sentry] Error details:", {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      });
    },
    
  });
}

// Log if Sentry DSN is missing (for debugging)
if (!import.meta.env.VITE_SENTRY_DSN) {
  console.warn("[Sentry] VITE_SENTRY_DSN is not set - Sentry will not be initialized");
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthRedirectHandler />
    <App />
  </StrictMode>
);
